import { logoutUser } from '@redux/auth/authSlice';
import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { merchantAuthApi, onboardingApi } from '@services/api';
import encryptedStorage from '@storage/encryptedStorage';
import { getApiErrorMessage } from '@utils/apiError';
import { getMerchantDeviceHeaders } from '@utils/deviceHeaders';

const PREFERENCE_OPTIONS = {
  cuisines: [
    { id: 'italian', label: 'Italian', emoji: '🍝' },
    { id: 'chinese', label: 'Chinese', emoji: '🥡' },
    { id: 'mexican', label: 'Mexican', emoji: '🌮' },
    { id: 'indian', label: 'Indian', emoji: '🍛' },
    { id: 'japanese', label: 'Japanese', emoji: '🍣' },
    { id: 'thai', label: 'Thai', emoji: '🍜' },
    { id: 'american', label: 'American', emoji: '🍔' },
    { id: 'mediterranean', label: 'Mediterranean', emoji: '🥙' },
    { id: 'korean', label: 'Korean', emoji: '🍲' },
    { id: 'vietnamese', label: 'Vietnamese', emoji: '🍲' },
    { id: 'french', label: 'French', emoji: '🥐' },
    { id: 'middle-eastern', label: 'Middle Eastern', emoji: '🧆' },
  ],
  dietary: [
    { id: 'vegetarian', label: 'Vegetarian', emoji: '🥗' },
    { id: 'vegan', label: 'Vegan', emoji: '🌱' },
    { id: 'gluten-free', label: 'Gluten-Free', emoji: '🌾' },
    { id: 'halal', label: 'Halal', emoji: '☪️' },
    { id: 'kosher', label: 'Kosher', emoji: '✡️' },
    { id: 'dairy-free', label: 'Dairy-Free', emoji: '🥛' },
  ],
  diningStyles: [
    { id: 'dine-in', label: 'Dine In', emoji: '🍽️' },
    { id: 'takeout', label: 'Takeout', emoji: '🥡' },
    { id: 'delivery', label: 'Delivery', emoji: '🛵' },
    { id: 'fine-dining', label: 'Fine Dining', emoji: '✨' },
    { id: 'fast-casual', label: 'Fast Casual', emoji: '⚡' },
    { id: 'food-trucks', label: 'Food Trucks', emoji: '🚚' },
  ],
};

export const saveCreateProfile = createAsyncThunk(
  'onboarding/saveProfile',
  async (profile, { rejectWithValue }) => {
    try {
      const headers = await getMerchantDeviceHeaders();
      const profilePicUrl = typeof profile.profileImage === 'string' ? profile.profileImage : '';

      const response = await merchantAuthApi.register(
        {
          firstName: profile.firstName,
          lastName: profile.lastName,
          email: profile.email,
          profilePicUrl,
        },
        headers,
      );

      const body = response?.data;
      if (body && typeof body === 'object' && body.success === false) {
        const message =
          (typeof body.data?.errorMessage === 'string' && body.data.errorMessage) ||
          (typeof body.message === 'string' && body.message) ||
          'Failed to save profile';
        return rejectWithValue(message);
      }

      return body;
    } catch (error) {
      return rejectWithValue(getApiErrorMessage(error, 'Failed to save profile'));
    }
  },
);

export const saveDinePreferences = createAsyncThunk(
  'onboarding/savePreferences',
  async (preferences, { rejectWithValue }) => {
    try {
      const response = await onboardingApi.savePreferences(preferences);
      return response.data;
    } catch (error) {
      return rejectWithValue(getApiErrorMessage(error, 'Failed to save preferences'));
    }
  },
);

export const submitBusinessClaim = createAsyncThunk(
  'onboarding/submitBusinessClaim',
  async (payload, { rejectWithValue }) => {
    try {
      const placeId = typeof payload === 'string' ? payload : payload?.placeId;
      const name = typeof payload === 'string' ? '' : payload?.name || '';
      const address = typeof payload === 'string' ? '' : payload?.address || '';
      const photo = typeof payload === 'string' ? '' : payload?.photo || '';

      if (!placeId) {
        return rejectWithValue('Place ID is required to claim business');
      }

      const response = await merchantAuthApi.syncRestaurant({
        placeId,
        name,
        address,
        photo,
      });
      return response.data;
    } catch (error) {
      return rejectWithValue(getApiErrorMessage(error, 'Failed to claim business'));
    }
  },
);

const onboardingSlice = createSlice({
  name: 'onboarding',
  initialState: {
    profile: {
      firstName: '',
      lastName: '',
      email: '',
      profileImagePreview: '',
    },
    profileCompleted: !!encryptedStorage.getItem('profileCompleted'),
    isNewUser: Boolean(encryptedStorage.getItem('onboardingIsNewUser')),
    businessSearch: {
      query: '',
      selectedBusiness: null,
      claimSubmitted: !!encryptedStorage.getItem('claimSubmitted'),
    },
    selectedPreferences: {
      cuisines: [],
      dietary: [],
      diningStyles: [],
    },
    options: PREFERENCE_OPTIONS,
    loading: false,
    error: null,
  },
  reducers: {
    togglePreference: (state, action) => {
      const { category, id } = action.payload;
      const list = state.selectedPreferences[category] || [];
      const index = list.indexOf(id);
      if (index >= 0) {
        state.selectedPreferences[category] = list.filter((item) => item !== id);
      } else {
        state.selectedPreferences[category] = [...list, id];
      }
    },
    setPreferences: (state, action) => {
      state.selectedPreferences = { ...state.selectedPreferences, ...action.payload };
    },
    clearPreferences: (state) => {
      state.selectedPreferences = { cuisines: [], dietary: [], diningStyles: [] };
      state.error = null;
    },
    clearOnboarding: (state) => {
      state.profile = { firstName: '', lastName: '', email: '', profileImagePreview: '' };
      state.profileCompleted = false;
      state.isNewUser = false;
      state.businessSearch = { query: '', selectedBusiness: null, claimSubmitted: false };
      state.selectedPreferences = { cuisines: [], dietary: [], diningStyles: [] };
      state.error = null;
      encryptedStorage.removeItem('profileCompleted');
      encryptedStorage.removeItem('onboardingIsNewUser');
      encryptedStorage.removeItem('claimSubmitted');
    },
    setBusinessSearchQuery: (state, action) => {
      state.businessSearch.query = action.payload;
    },
    setSelectedBusiness: (state, action) => {
      state.businessSearch.selectedBusiness = action.payload;
    },
    clearSelectedBusiness: (state) => {
      state.businessSearch.selectedBusiness = null;
    },
    setProfileCompleted: (state, action) => {
      state.profileCompleted = Boolean(action.payload);
      if (state.profileCompleted) {
        encryptedStorage.setItem('profileCompleted', true);
      } else {
        encryptedStorage.removeItem('profileCompleted');
      }
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(saveCreateProfile.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(saveCreateProfile.fulfilled, (state, action) => {
        state.loading = false;
        const profile = action.meta.arg;
        const profilePicUrl = typeof profile.profileImage === 'string' ? profile.profileImage : '';
        state.profile = {
          firstName: profile.firstName || '',
          lastName: profile.lastName || '',
          email: profile.email || '',
          profileImagePreview: profilePicUrl || state.profile.profileImagePreview,
        };
        state.profileCompleted = true;
        encryptedStorage.setItem('profileCompleted', true);
        const payload = action.payload;
        const data = payload?.data || payload;
        const isNewUser = Boolean(data?.isNewUser ?? data?.user?.isNewUser);
        state.isNewUser = isNewUser;
        encryptedStorage.setItem('onboardingIsNewUser', isNewUser);
      })
      .addCase(saveCreateProfile.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(saveDinePreferences.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(saveDinePreferences.fulfilled, (state) => {
        state.loading = false;
      })
      .addCase(saveDinePreferences.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(submitBusinessClaim.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(submitBusinessClaim.fulfilled, (state) => {
        state.loading = false;
        state.businessSearch.claimSubmitted = true;
        encryptedStorage.setItem('claimSubmitted', true);
      })
      .addCase(submitBusinessClaim.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(logoutUser.fulfilled, (state) => {
        state.profile = { firstName: '', lastName: '', email: '', profileImagePreview: '' };
        state.profileCompleted = false;
        state.isNewUser = false;
        state.businessSearch = { query: '', selectedBusiness: null, claimSubmitted: false };
        state.selectedPreferences = { cuisines: [], dietary: [], diningStyles: [] };
        state.error = null;
        encryptedStorage.removeItem('profileCompleted');
        encryptedStorage.removeItem('onboardingIsNewUser');
        encryptedStorage.removeItem('claimSubmitted');
      });
  },
});

export const {
  togglePreference,
  setPreferences,
  clearPreferences,
  clearOnboarding,
  setBusinessSearchQuery,
  setSelectedBusiness,
  clearSelectedBusiness,
  setProfileCompleted,
} = onboardingSlice.actions;
export default onboardingSlice.reducer;
